import {
  parseWorkspaceDocument,
  type WorkspaceDocument,
} from "./workspace.document";

export const WORKSPACE_CHANNEL_NAME = "workspace-rgl-shared-v3";

export interface WorkspaceMessage {
  writerId: string;
  document: WorkspaceDocument;
}

export interface WorkspaceChannel {
  post: (message: WorkspaceMessage) => void;
  subscribe: (listener: (message: WorkspaceMessage) => void) => () => void;
  close: () => void;
}

type Endpoint = {
  listeners: Set<(message: WorkspaceMessage) => void>;
};

const buses = new Map<string, Set<Endpoint>>();

const parseMessage = (value: unknown): WorkspaceMessage | undefined => {
  if (
    typeof value !== "object" ||
    value === null ||
    !("writerId" in value) ||
    typeof value.writerId !== "string" ||
    !("document" in value)
  ) {
    return undefined;
  }
  const document = parseWorkspaceDocument(value.document);
  return document ? { writerId: value.writerId, document } : undefined;
};

export const createInProcessWorkspaceChannel = (
  name = WORKSPACE_CHANNEL_NAME,
): WorkspaceChannel => {
  const endpoint: Endpoint = { listeners: new Set() };
  const endpoints = buses.get(name) ?? new Set();
  endpoints.add(endpoint);
  buses.set(name, endpoints);

  return {
    post: (value) => {
      const message = parseMessage(value);
      if (!message) {
        return;
      }
      for (const peer of buses.get(name) ?? []) {
        if (peer === endpoint) {
          continue;
        }
        for (const listener of [...peer.listeners]) {
          listener(message);
        }
      }
    },
    subscribe: (listener) => {
      endpoint.listeners.add(listener);
      return () => endpoint.listeners.delete(listener);
    },
    close: () => {
      endpoint.listeners.clear();
      const endpoints = buses.get(name);
      endpoints?.delete(endpoint);
      if (endpoints?.size === 0) {
        buses.delete(name);
      }
    },
  };
};

const createBroadcastWorkspaceChannel = (name: string): WorkspaceChannel => {
  const channel = new BroadcastChannel(name);
  const listeners = new Set<(message: WorkspaceMessage) => void>();
  const onMessage = (event: MessageEvent<unknown>) => {
    const message = parseMessage(event.data);
    if (!message) {
      return;
    }
    for (const listener of [...listeners]) {
      listener(message);
    }
  };
  channel.addEventListener("message", onMessage);

  return {
    post: (message) => {
      try {
        channel.postMessage(message);
      } catch {
        // A storage event or the next locked read will reconcile a close race.
      }
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    close: () => {
      listeners.clear();
      channel.removeEventListener("message", onMessage);
      channel.close();
    },
  };
};

export const createWorkspaceChannel = (
  name = WORKSPACE_CHANNEL_NAME,
): WorkspaceChannel =>
  typeof BroadcastChannel === "function"
    ? createBroadcastWorkspaceChannel(name)
    : createInProcessWorkspaceChannel(name);
